/**
 * The header line that says what an enrichment run *is*.
 *
 * Everything the scrape header shows about provenance — trigger type, sites —
 * is constant on an enrichment run: `trigger_type` is `manual` on all of them
 * (there is no enrichment trigger type) and the only site is the
 * `system-enrichment` sentinel. The axes that vary live on `trigger_spec`:
 * which intake asked (`source`), for how many entities, and on whose behalf.
 *
 * Same three fields the runs list's Enrichments tab shows per row, so a curator
 * who clicks through from the list sees the row they clicked — plus, on a
 * dependent repair, the parent that caused the run.
 */
import { ArrowUpRight, GitBranch } from "lucide-react";

import { UserAvatar } from "@/components/common/user-avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import {
  enrichmentEntityHref,
  enrichmentEntityLabel,
  enrichmentSourceLabel,
} from "@/lib/tina/enrichment-jobs";
import { triggeredByLabel } from "@/pages/tina/lib/run-attribution";
import type {
  EnrichmentRunParent,
  EnrichmentRunSpec,
} from "@/pages/tina/runs/runs-filters";

export function EnrichmentRunBadge() {
  return (
    <Badge className="bg-teal-500/15 text-teal-700 hover:bg-teal-500/15 dark:text-teal-300">
      enrichment
    </Badge>
  );
}

export function EnrichmentRunMeta({ spec }: { spec: EnrichmentRunSpec }) {
  const { members } = useMembers();
  const requester = spec.requestedBy
    ? members.find((member) => member.id === spec.requestedBy)
    : undefined;
  // `requested_by` is a uid only when a human asked; the rest of the time it is
  // a system provenance string (`cron-backstop`, `tina-walk:<uuid>`) that
  // `triggeredByLabel` passes through unchanged rather than inventing a name.
  //
  // On a repair run it is `repair:<type>:<id>` — the same reference the parent
  // chip renders as a link, so printing it too would be the raw form of a thing
  // already on screen.
  const who =
    spec.requestedBy && !spec.parent
      ? triggeredByLabel(spec.requestedBy, "manual", members)
      : null;

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
      <span className="inline-flex items-center gap-1.5">
        <span className="uppercase tracking-[0.08em] text-[10px]">source</span>
        <Badge variant="outline" className="font-normal">
          {enrichmentSourceLabel(spec.source)}
        </Badge>
      </span>
      {spec.entityCount !== null && (
        <>
          <Separator orientation="vertical" className="h-3" />
          <span className="font-mono tabular-nums">
            {spec.entityCount.toLocaleString()}{" "}
            {spec.entityCount === 1 ? "entity" : "entities"} requested
          </span>
        </>
      )}
      {who && (
        <>
          <Separator orientation="vertical" className="h-3" />
          <span className="inline-flex items-center gap-1.5">
            by
            {requester ? (
              <UserAvatar {...requester} half />
            ) : (
              <span className="font-mono">{who}</span>
            )}
          </span>
        </>
      )}
      {spec.parent && (
        <>
          <Separator orientation="vertical" className="h-3" />
          <RepairParent parent={spec.parent} />
        </>
      )}
    </div>
  );
}

/**
 * The entity whose own enrichment forced this run.
 *
 * A dependent repair is the second-biggest enrichment source in prod (699 runs
 * on 2026-09-02) and the only one whose rows make no sense on their own: the
 * page listed four events being re-enriched and never said that an artist
 * changing underneath them is why. Both links matter — the entity is what a
 * curator checks, the parent job row is where the change came from.
 */
function RepairParent({ parent }: { parent: EnrichmentRunParent }) {
  const href = enrichmentEntityHref(parent.entityType, parent.entityId);
  const label = enrichmentEntityLabel(parent.entityType);
  const chip = (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Badge variant="secondary" className="text-[10px] font-normal">
        {label}
      </Badge>
      <span className="max-w-[160px] truncate font-mono">
        {parent.entityId}
      </span>
      {href && <ArrowUpRight className="h-3 w-3 shrink-0" />}
    </span>
  );

  return (
    <span className="inline-flex items-center gap-1.5">
      <GitBranch className="h-3 w-3" />
      <span>repairs dependents of</span>
      {makeTooltip(
        href ? (
          <a href={href} className="min-w-0 hover:underline">
            {chip}
          </a>
        ) : (
          chip
        ),
        `${label} ${parent.entityId}`,
      )}
      {parent.parentEventId && (
        <a
          href={`/tina/items/show/${parent.parentEventId}`}
          className="text-blue-500 hover:underline"
        >
          parent job
        </a>
      )}
    </span>
  );
}
